import { AlertTriangle, RefreshCw, Search, ShieldCheck, XCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  computeAlertStatus,
  CONTRACT_STATUS,
  CONTRACT_TYPES,
  SLA_TIERS,
} from './mockContractData';

function SlaBadge({ tier }) {
  const config = SLA_TIERS[tier];
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ring-1 ${config.className}`}
    >
      {config.label}
    </span>
  );
}

function AlertBadge({ expiryDate, terminated }) {
  if (terminated) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-0.5 text-[10px] font-bold uppercase text-logi-muted ring-1 ring-logi-grid">
        Terminated
      </span>
    );
  }
  const alert = computeAlertStatus(expiryDate);
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ring-1 ${alert.className}`}
      title={`Auto-alert: ${alert.hint}`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${alert.dotClass} ${alert.level === 'critical' ? 'animate-pulse' : ''}`}
      />
      {alert.label}
      <span className="font-normal normal-case opacity-80">· {alert.hint}</span>
    </span>
  );
}

function ContractStatusBadge({ status }) {
  const config = CONTRACT_STATUS[status];
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ring-1 ${config.className}`}
    >
      {config.label}
    </span>
  );
}

export default function ContractLedgerTable({
  contracts,
  contractType,
  onRenew,
  onTerminate,
}) {
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [terminatingId, setTerminatingId] = useState(null);
  const [terminateNote, setTerminateNote] = useState('');
  const typeMeta = CONTRACT_TYPES[contractType];

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return contracts.filter((c) => {
      const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
      const matchesQuery =
        c.partyName.toLowerCase().includes(q) ||
        c.id.toLowerCase().includes(q) ||
        SLA_TIERS[c.slaTier].label.toLowerCase().includes(q);
      return matchesStatus && matchesQuery;
    });
  }, [contracts, query, statusFilter]);

  const urgentCount = contracts.filter((c) => {
    if (c.status !== 'active') return false;
    const s = computeAlertStatus(c.expiryDate);
    return s.level === 'critical' || s.level === 'expired';
  }).length;

  const handleTerminateSubmit = (id) => {
    onTerminate?.(id, terminateNote.trim());
    setTerminatingId(null);
    setTerminateNote('');
  };

  return (
    <section className="overflow-hidden rounded-xl border border-logi-grid bg-white backdrop-blur-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-logi-grid px-4 py-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-emerald-700" strokeWidth={1.75} />
          <div>
            <h3 className="text-sm font-bold text-logi-text">{typeMeta.label}</h3>
            <p className="text-[11px] text-logi-text0">{typeMeta.sublabel}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {urgentCount > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-1 text-[10px] font-bold text-red-700 ring-1 ring-red-500/25">
              <AlertTriangle className="h-3 w-3" />
              {urgentCount} urgent
            </span>
          )}
          <div className="flex gap-1 rounded-lg border border-logi-grid bg-white p-0.5">
            {['active', 'terminated', 'all'].map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setStatusFilter(key)}
                className={`rounded-md px-2 py-1 text-[10px] font-bold uppercase transition ${
                  statusFilter === key
                    ? 'bg-logi-accent text-white'
                    : 'text-logi-text0 hover:text-logi-muted'
                }`}
              >
                {key}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-48">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-logi-text0" />
            <input
              type="search"
              placeholder="Filter contracts…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full rounded-lg border border-logi-grid bg-white py-1.5 pl-8 pr-3 text-xs text-logi-text placeholder-logi-subtle outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30"
            />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[960px] text-left text-sm">
          <thead>
            <tr className="border-b border-logi-grid/80 bg-white text-[10px] uppercase tracking-wider text-logi-text0">
              <th className="px-4 py-2.5 font-semibold">Contract ID</th>
              <th className="px-4 py-2.5 font-semibold">Party Name</th>
              <th className="px-4 py-2.5 font-semibold">SLA Tier</th>
              <th className="px-4 py-2.5 font-semibold">Start Date</th>
              <th className="px-4 py-2.5 font-semibold">Expiry Date</th>
              <th className="px-4 py-2.5 font-semibold">Auto-Alert</th>
              <th className="px-4 py-2.5 font-semibold">Status</th>
              <th className="px-4 py-2.5 font-semibold">Workflow</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-logi-grid/30">
            {filtered.map((contract) => {
              const alert = computeAlertStatus(contract.expiryDate);
              const isUrgent =
                contract.status === 'active' &&
                (alert.level === 'critical' || alert.level === 'expired');
              const isTerminating = terminatingId === contract.id;

              return (
                <tr
                  key={contract.id}
                  className={`transition-colors hover:bg-logi-silver ${
                    isUrgent ? 'bg-red-500/[0.03]' : contract.status === 'terminated' ? 'opacity-60' : ''
                  }`}
                >
                  <td className="px-4 py-2.5">
                    <span className="font-mono text-xs font-semibold text-emerald-700/90">
                      {contract.id}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 font-medium text-logi-text">{contract.partyName}</td>
                  <td className="px-4 py-2.5">
                    <SlaBadge tier={contract.slaTier} />
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-logi-muted">
                    {contract.startDate}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-logi-muted">
                    {contract.expiryDate}
                  </td>
                  <td className="px-4 py-2.5">
                    <AlertBadge
                      expiryDate={contract.expiryDate}
                      terminated={contract.status === 'terminated'}
                    />
                  </td>
                  <td className="px-4 py-2.5">
                    <ContractStatusBadge status={contract.status} />
                  </td>
                  <td className="px-4 py-2.5">
                    {contract.status === 'terminated' ? (
                      <span className="text-[10px] text-logi-text0">—</span>
                    ) : isTerminating ? (
                      <div className="flex min-w-[200px] flex-col gap-1.5">
                        <input
                          type="text"
                          placeholder="Termination reason (optional)…"
                          value={terminateNote}
                          onChange={(e) => setTerminateNote(e.target.value)}
                          className="w-full rounded border border-logi-grid bg-white px-2 py-1 text-[10px] text-logi-text outline-none focus:border-red-500/50"
                        />
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() => handleTerminateSubmit(contract.id)}
                            className="inline-flex items-center gap-1 rounded bg-red-600/20 px-2 py-1 text-[10px] font-bold uppercase text-red-700 ring-1 ring-red-500/30 hover:bg-red-600/30"
                          >
                            <XCircle className="h-3 w-3" />
                            Confirm
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setTerminatingId(null);
                              setTerminateNote('');
                            }}
                            className="rounded px-2 py-1 text-[10px] text-logi-text0 hover:text-logi-muted"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        <button
                          type="button"
                          onClick={() => onRenew?.(contract.id)}
                          className="inline-flex items-center gap-1 rounded bg-emerald-600/15 px-2 py-1 text-[10px] font-bold uppercase text-emerald-700 ring-1 ring-emerald-500/25 transition hover:bg-emerald-600/25"
                        >
                          <RefreshCw className="h-3 w-3" />
                          Renew
                        </button>
                        <button
                          type="button"
                          onClick={() => setTerminatingId(contract.id)}
                          className="inline-flex items-center gap-1 rounded bg-red-600/15 px-2 py-1 text-[10px] font-bold uppercase text-red-700 ring-1 ring-red-500/25 transition hover:bg-red-600/25"
                        >
                          <XCircle className="h-3 w-3" />
                          Terminate
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filtered.length === 0 && (
        <p className="px-4 py-8 text-center text-sm text-logi-text0">No contracts match your filter.</p>
      )}
    </section>
  );
}
